import type { FilterOptionOption } from 'react-select';
import type { DropdownOption } from '../utils/optionTransformers';
import { stripDiacritics } from './diacritics';

interface Config<Option> {
  readonly ignoreAccents?: boolean;
  readonly ignoreCase?: boolean;
  readonly matchFrom?: 'any' | 'start';
  readonly stringify?: (option: FilterOptionOption<Option>) => string;
  readonly trim?: boolean;
}

const trimString = (str: string): string => str.replace(/^\s+|\s+$/g, '');
const defaultStringify = (option: FilterOptionOption<DropdownOption<unknown>>) =>
  `${option.label} ${option.value}`;

/**
 * We made our own `createFilter` function with the builtin version
 *
 * Based on https://github.com/JedWatson/react-select @ `b867ff7eb5c02719cabd0db359cd34c6cbfb1c44`
 *
 *
 * Our patch changes the function to also check for matches in `option.value`
 *
 * Additions:
 * ```
 *   let optionValue = option.value;
 *       optionValue = option.value.toLowerCase();
 *       optionValue.substr(0, input.length) === input
 * ```
 */
export const createFilter =
  (config: Config<unknown>) =>
  (option: FilterOptionOption<DropdownOption<unknown>>, rawInput: string): boolean => {
    const {
      ignoreAccents,
      ignoreCase,
      stringify,
      trim,
      matchFrom,
    }: Config<DropdownOption<unknown>> = {
      ignoreCase: true,
      ignoreAccents: true,
      stringify: defaultStringify,
      trim: true,
      matchFrom: 'any',
      ...config,
    };
    let input = trim ? trimString(rawInput) : rawInput;
    let candidate = trim ? trimString(stringify(option)) : stringify(option);
    let optionValue = option.value;
    if (ignoreCase) {
      input = input.toLowerCase();
      optionValue = option.value.toLowerCase();
      candidate = candidate.toLowerCase();
    }
    if (ignoreAccents) {
      input = stripDiacritics(input);
      candidate = stripDiacritics(candidate);
    }

    return (
      (matchFrom === 'start'
        ? candidate.substr(0, input.length) === input
        : candidate.indexOf(input) > -1) || optionValue.substr(0, input.length) === input
    );
  };

export const dropdownFilterConfig: Config<unknown> = {
  matchFrom: 'start',
};

export const baseFilterOption = (
  option: FilterOptionOption<DropdownOption<unknown>>,
  rawInput: string,
): boolean => {
  const input = stripDiacritics(trimString(rawInput).toLowerCase());
  const candidate = stripDiacritics(trimString(defaultStringify(option)).toLowerCase());
  const optionValue = option.value.toLowerCase();

  return candidate.slice(0, input.length) === input || optionValue.slice(0, input.length) === input;
};
